import { useState, useEffect } from 'react'
import { useSocket } from '@/lib/socket'
import { useToast } from '@/hooks/use-toast'

interface Notification {
  id: string
  type: 'order' | 'payment' | 'emergency' | 'system' | 'promotion'
  title: string
  message: string
  timestamp: string
  read: boolean
  priority: 'low' | 'medium' | 'high' | 'urgent'
  actionUrl?: string
  actionText?: string
  metadata?: Record<string, any>
}

export function useNotifications() {
  const { socket, isConnected } = useSocket()
  const { toast } = useToast()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadInitialNotifications()
  }, [])

  useEffect(() => {
    if (isConnected && socket) {
      // Listen for real-time notifications
      socket.on('notification:new', handleNewNotification)
      socket.on('notification:update', handleNotificationUpdate)
      socket.on('order:new', handleNewOrderNotification)
      socket.on('order:cancelled', handleOrderCancelledNotification)
      socket.on('payment:completed', handlePaymentNotification)
      socket.on('delivery:completed', handleDeliveryCompletedNotification)
      socket.on('emergency:alert', handleEmergencyNotification)
      socket.on('surge:activated', handleSurgeNotification)

      return () => {
        socket.off('notification:new')
        socket.off('notification:update')
        socket.off('order:new')
        socket.off('order:cancelled')
        socket.off('payment:completed')
        socket.off('delivery:completed')
        socket.off('emergency:alert')
        socket.off('surge:activated')
      }
    }
  }, [isConnected, socket])

  useEffect(() => {
    // Update unread count when notifications change
    setUnreadCount(notifications.filter(n => !n.read).length)
  }, [notifications])

  const loadInitialNotifications = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/v1/notifications?limit=50', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })

      if (response.ok) {
        const data = await response.json()
        setNotifications(data.data?.notifications || [])
      }
    } catch (error) {
      console.error('Failed to load notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleNewNotification = (notification: Notification) => {
    setNotifications(prev => [notification, ...prev])

    // Show toast for important notifications
    if (notification.priority === 'high' || notification.priority === 'urgent') {
      toast({
        title: notification.title,
        description: notification.message,
        variant: notification.type === 'emergency' ? 'destructive' : 'default'
      })
    }

    // Play notification sound
    playNotificationSound(notification.priority)

    // Request notification permission and show browser notification
    showBrowserNotification(notification)
  }

  const handleNotificationUpdate = (updatedNotification: Notification) => {
    setNotifications(prev =>
      prev.map(n => n.id === updatedNotification.id ? updatedNotification : n)
    )
  }

  const handleNewOrderNotification = (orderData: any) => {
    const notification: Notification = {
      id: `order_${orderData.orderId}_${Date.now()}`,
      type: 'order',
      title: '🚚 New Order Available!',
      message: `₦${orderData.estimatedEarnings} - ${orderData.distance}km away from ${orderData.vendorName}`,
      timestamp: new Date().toISOString(),
      read: false,
      priority: 'high',
      actionUrl: '/active-orders',
      actionText: 'View Order',
      metadata: {
        orderId: orderData.orderId,
        amount: orderData.estimatedEarnings,
        distance: orderData.distance
      }
    }

    handleNewNotification(notification)
  }

  const handleOrderCancelledNotification = (orderData: any) => {
    const notification: Notification = {
      id: `order_cancelled_${orderData.orderId}_${Date.now()}`,
      type: 'order',
      title: '❌ Order Cancelled',
      message: `Order #${orderData.orderNumber} has been cancelled`,
      timestamp: new Date().toISOString(),
      read: false,
      priority: 'medium',
      metadata: {
        orderId: orderData.orderId
      }
    }

    handleNewNotification(notification)
  }

  const handlePaymentNotification = (paymentData: any) => {
    const notification: Notification = {
      id: `payment_${paymentData.paymentId}_${Date.now()}`,
      type: 'payment',
      title: '💰 Payment Received!',
      message: `₦${paymentData.amount?.toLocaleString()} has been added to your wallet`,
      timestamp: new Date().toISOString(),
      read: false,
      priority: 'medium',
      actionUrl: '/wallet',
      actionText: 'View Wallet',
      metadata: {
        amount: paymentData.amount
      }
    }

    handleNewNotification(notification)
  }

  const handleDeliveryCompletedNotification = (deliveryData: any) => {
    const notification: Notification = {
      id: `delivery_completed_${deliveryData.orderId}_${Date.now()}`,
      type: 'order',
      title: '✅ Delivery Completed!',
      message: `You earned ₦${deliveryData.earnings?.toLocaleString()} for order #${deliveryData.orderNumber}`,
      timestamp: new Date().toISOString(),
      read: false,
      priority: 'medium',
      actionUrl: '/earnings',
      actionText: 'View Earnings',
      metadata: {
        orderId: deliveryData.orderId,
        earnings: deliveryData.earnings
      }
    }

    handleNewNotification(notification)
  }

  const handleEmergencyNotification = (emergencyData: any) => {
    const notification: Notification = {
      id: `emergency_${emergencyData.alertId}_${Date.now()}`,
      type: 'emergency',
      title: '🚨 Emergency Alert',
      message: emergencyData.message || 'Emergency services have been notified',
      timestamp: new Date().toISOString(),
      read: false,
      priority: 'urgent',
      actionUrl: '/emergency',
      actionText: 'View Emergency'
    }

    handleNewNotification(notification)
  }

  const handleSurgeNotification = (surgeData: any) => {
    const notification: Notification = {
      id: `surge_${Date.now()}`,
      type: 'promotion',
      title: '🔥 Surge Pricing Active!',
      message: `${surgeData.multiplier}x surge in your area - Higher earnings available!`,
      timestamp: new Date().toISOString(),
      read: false,
      priority: 'high',
      actionUrl: '/active-orders',
      actionText: 'Find Orders',
      metadata: {
        multiplier: surgeData.multiplier,
        location: surgeData.location
      }
    }

    handleNewNotification(notification)
  }

  const playNotificationSound = (priority: string) => {
    try {
      const audio = new Audio()

      switch (priority) {
        case 'urgent':
          audio.src = '/sounds/urgent-notification.mp3'
          break
        case 'high':
          audio.src = '/sounds/high-notification.mp3'
          break
        default:
          audio.src = '/sounds/notification.mp3'
          break
      }

      audio.volume = 0.5
      audio.play().catch(() => {
        // Ignore audio play errors (user might not have interacted yet)
      })
    } catch (error) {
      // Ignore audio errors
    }
  }

  const showBrowserNotification = async (notification: Notification) => {
    if (!('Notification' in window)) return

    try {
      let permission = Notification.permission

      if (permission === 'default') {
        permission = await Notification.requestPermission()
      }

      if (permission === 'granted' && (notification.priority === 'high' || notification.priority === 'urgent')) {
        new Notification(notification.title, {
          body: notification.message,
          icon: '/icon-192x192.png',
          badge: '/icon-192x192.png',
          tag: notification.id,
          requireInteraction: notification.priority === 'urgent'
        })
      }
    } catch (error) {
      console.log('Browser notification failed:', error)
    }
  }

  const markAsRead = async (notificationId: string) => {
    try {
      await fetch(`/api/v1/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })

      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      )
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }
  }

  const markAllAsRead = async () => {
    try {
      await fetch('/api/v1/notifications/mark-all-read', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })

      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error)
    }
  }

  const deleteNotification = async (notificationId: string) => {
    try {
      await fetch(`/api/v1/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })

      setNotifications(prev => prev.filter(n => n.id !== notificationId))
    } catch (error) {
      console.error('Failed to delete notification:', error)
    }
  }

  const clearAllNotifications = async () => {
    try {
      await fetch('/api/v1/notifications/clear-all', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })

      setNotifications([])
    } catch (error) {
      console.error('Failed to clear all notifications:', error)
    }
  }

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    refreshNotifications: loadInitialNotifications
  }
}